/**
 * MonthlyWorkHoursReport — Raporti Mujor Individual i Orëve të Punës
 *
 * Shfaq planifikuar vs actual orë pune për punonjës me tabela ditore
 * dhe përmbledhje javore. Admin/menaxheri mund të korrigjojnë ditë.
 *
 * Përdor: GET /api/reports/attendance-daily?worker_id=X&year=Y&month=M
 *
 * Chrome-i vjen nga `report/ReportKit` si te faqet e tjera të raporteve:
 * shiriti i filtrave, kutitë e shifrave, eksporti si një buton me menu dhe
 * gjendjet bosh/ngarkim brenda tabelës. Më parë kjo faqe i rindërtonte të
 * gjitha vetë — tre butona eksporti me tre ngjyra dhe një shirit KPI me tetë
 * hex-e të hardkoduara, pra pikërisht ylberi që kiti erdhi ta heqë.
 */
import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, TextField, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody,
  IconButton, Tooltip, Avatar,
} from '@mui/material';
import EditIcon          from '@mui/icons-material/Edit';
import ChevronLeftIcon   from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon  from '@mui/icons-material/ChevronRight';
import PersonIcon        from '@mui/icons-material/Person';
import AttendanceAdjustmentDialog from './AttendanceAdjustmentDialog';
import * as XLSX         from 'xlsx';
import jsPDF             from 'jspdf';
import autoTable         from 'jspdf-autotable';
import { useTranslation } from 'react-i18next';
import { getAuthHeaders, getLogedInUser } from '../../services/authSession';
import { API_URL } from '../../services/backendMode';
import { wasEmployedOn } from '../../utils/workerStatus';
import {
  ReportPage, ReportFilters, StatTiles, ReportCard, ReportTotals,
  ExportMenu, LoadingRow, EmptyRow,
} from '../report/ReportKit';

// ── Constants ─────────────────────────────────────────────────────────────────

const thisYear  = new Date().getFullYear();
const thisMonth = new Date().getMonth() + 1;
const YEARS     = Array.from({ length: 5 }, (_, i) => thisYear - 2 + i);


// ── Helpers ───────────────────────────────────────────────────────────────────

const getCurrentUser = getLogedInUser;

const pad2 = (n) => String(n).padStart(2, '0');

/** Format minutes → H:mm */
function fmtHM(min) {
  const m = Math.max(0, Math.round(Number(min) || 0));
  return `${Math.floor(m / 60)}:${('0' + (m % 60)).slice(-2)}`;
}

/** Format minute-difference → ±H:mm */
function fmtDiff(min) {
  if (min == null || isNaN(Number(min))) return '—';
  const n   = Number(min);
  const abs = Math.max(0, Math.round(Math.abs(n)));
  return `${n >= 0 ? '+' : '-'}${Math.floor(abs / 60)}:${('0' + (abs % 60)).slice(-2)}`;
}

/**
 * Format ISO timestamp → HH:mm, gjithmonë 24-orësh.
 * Pa `hour12: false`, locale-i `sq-AL` e jep orën si «06:00 e pasdites» — në një
 * fletë prezence ku pranë saj rri orari i planifikuar «08:00–16:00», e njëjta
 * kolonë lexohej me dy sisteme, dhe një dalje e vonë dukej si dalje e mëngjesit.
 */
function fmtTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d)
    ? (String(iso).slice(0, 5) || '—')
    : d.toLocaleTimeString('sq-AL', { hour: '2-digit', minute: '2-digit', hour12: false });
}

/** Format DD.MM from YYYY-MM-DD */
function dayFmt(dateStr) {
  if (!dateStr) return '';
  return `${dateStr.slice(8)}.${dateStr.slice(5, 7)}`;
}

/**
 * Çelësi i javës (e hëna) për një datë.
 * Data lexohet dhe kthehet me kalendar LOKAL: `toISOString` e zhvendos një ditë
 * prapa për çdo zonë lindje nga UTC (Prishtina është +1/+2), pra e hëna e javës
 * dilte e diela.
 */
function getMondayKey(dateStr) {
  const d   = new Date(dateStr + 'T00:00:00');
  const day = d.getDay(); // 0=Sun
  d.setDate(d.getDate() - (day === 0 ? 6 : day - 1));
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/** Worker display name, handles PascalCase and snake_case field names */
function workerFullName(w) {
  if (!w) return '';
  return `${w.FirstName || w.first_name || ''} ${w.LastName || w.last_name || ''}`.trim();
}

/** Id-ja e departamentit të një punëtori — API-ja e kthen si `Department`. */
const deptIdOf = (w) => String(w?.Department ?? w?.department_id ?? '');

// ── Sub-components ────────────────────────────────────────────────────────────

function WeeklySummaryRow({ weekRows, isAdmin }) {
  const { t } = useTranslation();
  const d1    = weekRows[0]?.date || '';
  const d2    = weekRows[weekRows.length - 1]?.date || '';
  const label = `${dayFmt(d1)} – ${dayFmt(d2)}`;

  const work    = weekRows.filter(r => r.is_work_day);
  const planned = work.reduce((s, r) => s + (Number(r.planned_minutes)     || 0), 0);
  const worked  = work.reduce((s, r) => s + (Number(r.net_worked_minutes)  || 0), 0);
  const diff    = worked - planned;
  const ot      = work.reduce((s, r) => s + (Number(r.overtime_minutes)    || 0), 0);
  const late    = work.reduce((s, r) => s + (Number(r.late_minutes)        || 0), 0);
  const absent  = work.filter(r => r.compliance_status === 'absent').length;

  return (
    <TableRow
      sx={{
        bgcolor: 'action.selected',
        '& td': {
          fontWeight: 700,
          py: '5px',
          borderBottom: '2px solid',
          borderColor: 'divider',
        },
      }}
    >
      <TableCell colSpan={2}>
        <Typography variant="caption" fontWeight={700} color="primary.main" noWrap>
          {t('report.week')}: {label}
        </Typography>
      </TableCell>
      <TableCell className="num">{fmtHM(planned)}</TableCell>
      <TableCell align="center" sx={{ color: 'text.disabled' }}>—</TableCell>
      <TableCell align="center" sx={{ color: 'text.disabled' }}>—</TableCell>
      <TableCell align="center" sx={{ color: 'text.disabled' }}>—</TableCell>
      <TableCell className="num">{fmtHM(worked)}</TableCell>
      <TableCell className="num" sx={{ color: diff >= 0 ? 'success.main' : 'error.main' }}>
        {fmtDiff(diff)}
      </TableCell>
      <TableCell align="center">
        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap">
          {ot     > 0 && <Chip size="small" label={`OT: ${fmtHM(ot)}`}                     color="info"    variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
          {late   > 0 && <Chip size="small" label={`${t('report.lateAbbr')}: ${late}m`}    color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
          {absent > 0 && <Chip size="small" label={`${t('report.absentAbbr')}: ${absent}`} color="error"   variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />}
          {!ot && !late && !absent && (
            <Chip size="small" label="OK" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
          )}
        </Stack>
      </TableCell>
      {isAdmin && <TableCell />}
    </TableRow>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function MonthlyWorkHoursReport() {
  const { t } = useTranslation();

  const STATUS_META = useMemo(() => ({
    on_time:        { label: t('report.present'),       color: 'success' },
    late:           { label: t('report.lateLabel'),     color: 'warning' },
    early_checkout: { label: t('report.earlyCheckout'), color: 'warning' },
    absent:         { label: t('report.absentLabel'),   color: 'error'   },
    in_progress:    { label: t('report.inProgress'),    color: 'info'    },
    day_off:        { label: t('report.dayOff'),        color: 'default' },
  }), [t]);

  const [year,       setYear]       = useState(thisYear);
  const [month,      setMonth]      = useState(thisMonth);
  const [fDept,      setFDept]      = useState('');
  const [fWorker,    setFWorker]    = useState('');
  const [workers,    setWorkers]    = useState([]);
  const [depts,      setDepts]      = useState([]);
  const [rows,       setRows]       = useState([]);
  const [workerInfo, setWorkerInfo] = useState(null);
  const [loading,    setLoading]    = useState(false);
  const [loaded,     setLoaded]     = useState(false);
  const [adjRow,     setAdjRow]     = useState(null);
  const [adjOpen,    setAdjOpen]    = useState(false);

  const isAdmin = useMemo(() => {
    const u    = getCurrentUser();
    const role = u?.data?.role || u?.role || '';
    return role !== 'worker';
  }, []);

  const autoLoadDoneRef = useRef(false);

  const loadFilters = useCallback(async () => {
    try {
      const [wRes, dRes] = await Promise.all([
        fetch(`${API_URL}/api/workers`,     { headers: getAuthHeaders() }).then(r => r.json()).catch(() => ({})),
        fetch(`${API_URL}/api/departments`, { headers: getAuthHeaders() }).then(r => r.json()).catch(() => ({})),
      ]);
      setWorkers(wRes?.data || []);
      setDepts(dRes?.data   || []);
    } catch (e) {
      console.error('[MonthlyWorkHoursReport] loadFilters:', e);
    }
  }, []);

  useEffect(() => { loadFilters(); }, [loadFilters]);

  /** Punëtori i vetë përdoruesit — parazgjedhja e faqes dhe pika ku kthehet «Pastro». */
  const defaultWorkerId = useCallback(() => {
    const u      = getCurrentUser();
    const userId = u?.data?.id || u?.id || u?.data?.workerId || u?.workerId;
    if (!userId) return '';
    const match = workers.find(w =>
      String(w.Id || w.id) === String(userId) ||
      String(w.user_id)    === String(userId)
    );
    return match ? String(match.Id || match.id) : '';
  }, [workers]);

  // Auto-select logged-in user's worker record on first load
  useEffect(() => {
    if (autoLoadDoneRef.current || workers.length === 0) return;
    const id = defaultWorkerId();
    if (!id) return;
    autoLoadDoneRef.current = true;
    setFWorker(id);
  }, [workers, defaultWorkerId]);

  // Filter workers by selected department
  //
  // Kush u largua nuk rri te lista për muajt që nuk ka punuar — por MBETET për
  // muajt që ka: fleta e prillit e dikujt që iku më 10 prill duhet të printohet
  // ende. Prandaj matet me datën e punësimit kundrejt ditës së parë të muajit,
  // jo me flamurin «aktiv», që s'ka datë brenda vetes.
  const filteredWorkers = useMemo(() => {
    const firstDay = `${year}-${String(month).padStart(2, '0')}-01`;
    const list = fDept ? workers.filter(w => deptIdOf(w) === String(fDept)) : workers;
    return list.filter(w => wasEmployedOn(w, firstDay));
  }, [workers, fDept, year, month]);

  // Load report data from backend
  const handleLoad = useCallback(async () => {
    if (!fWorker) return;
    setLoading(true);
    setLoaded(false);
    try {
      const res = await fetch(
        `${API_URL}/api/reports/attendance-daily?worker_id=${fWorker}&year=${year}&month=${month}`,
        { headers: getAuthHeaders() }
      );
      const j = await res.json().catch(() => ({}));
      const wid = j?.worker?.id || fWorker;
      // Attach worker_id to each row for the adjustment dialog
      setRows((j?.data || []).map(r => ({ ...r, worker_id: wid })));
      setWorkerInfo(j?.worker || null);
      setLoaded(true);
    } catch (e) {
      console.error('[MonthlyWorkHoursReport] load:', e);
      setRows([]);
      setWorkerInfo(null);
    } finally {
      setLoading(false);
    }
  }, [fWorker, year, month]);

  /**
   * Ngarko kur ndryshon punëtori/muaji — dhe PASTRO kur punëtori hiqet.
   * Pa degën e dytë, zgjedhja e një departamenti tjetër e zbraste listën e
   * punëtorëve por e linte në ekran tabelën e punëtorit të mëparshëm: faqja
   * tregonte të dhëna që s'i përgjigjeshin më asnjë filtri të dukshëm.
   */
  useEffect(() => {
    if (!fWorker) {
      setRows([]);
      setWorkerInfo(null);
      setLoaded(false);
      return;
    }
    handleLoad();
  }, [fWorker, year, month]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Departamenti e mban punëtorin nëse ai i përket — e heq vetëm nëse s'i përket. */
  const handleDept = (value) => {
    setFDept(value);
    if (!value) return;
    const stillVisible = workers.some(w =>
      String(w.Id || w.id) === String(fWorker) && deptIdOf(w) === String(value)
    );
    if (!stillVisible) setFWorker('');
  };

  const handleReset = () => {
    setFDept('');
    setYear(thisYear);
    setMonth(thisMonth);
    setFWorker(defaultWorkerId());
  };

  const handleRefresh = () => {
    loadFilters();
    if (fWorker) handleLoad();
  };

  const dirty = Boolean(fDept)
    || month !== thisMonth
    || year  !== thisYear
    || String(fWorker) !== String(defaultWorkerId());

  // Month navigation arrows
  const prevMonth = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  // KPI totals computed from daily rows
  const kpi = useMemo(() => {
    const wd = rows.filter(r => r.is_work_day);
    return {
      workDays: wd.length,
      planned:  wd.reduce((s, r) => s + (Number(r.planned_minutes)    || 0), 0),
      worked:   wd.reduce((s, r) => s + (Number(r.net_worked_minutes) || 0), 0),
      overtime: wd.reduce((s, r) => s + (Number(r.overtime_minutes)   || 0), 0),
      breaks:   wd.reduce((s, r) => s + (Number(r.break_minutes)      || 0), 0),
      late:     wd.reduce((s, r) => s + (Number(r.late_minutes)       || 0), 0),
      absent:   wd.filter(r => r.compliance_status === 'absent').length,
      adjusted: rows.filter(r => r.is_adjusted).length,
    };
  }, [rows]);

  const diffTotal = kpi.worked - kpi.planned;

  // Group days by ISO week (Mon–Sun)
  const weekGroups = useMemo(() => {
    const groups = [];
    rows.forEach(row => {
      const key  = getMondayKey(row.date);
      const last = groups[groups.length - 1];
      if (!last || last.key !== key) {
        groups.push({ key, rows: [] });
      }
      groups[groups.length - 1].rows.push(row);
    });
    return groups;
  }, [rows]);

  const wName       = workerFullName(workerInfo);
  const periodLabel = `${t('report.month_' + month)} ${year}`;
  const identity    = [
    workerInfo?.department_name,
    workerInfo?.schedule_name,
    workerInfo?.start_time && workerInfo?.end_time
      ? `${String(workerInfo.start_time).slice(0, 5)}–${String(workerInfo.end_time).slice(0, 5)}`
      : '',
  ].filter(Boolean).join(' · ');

  const COLS = isAdmin ? 10 : 9;

  // ── Eksporti ───────────────────────────────────────────────────────────────
  const fileBase = `ore-pune-${fWorker}-${year}-${pad2(month)}`;

  const EXPORT_HEADERS = [
    t('report.date'), t('report.dayName'), t('report.planned'),
    t('report.checkIn'), t('report.breakCol'), t('report.checkOut'),
    t('report.workedHours'), t('report.difference'), t('report.status'), t('report.corrected'),
  ];

  const exportRows = () => rows.map(r => [
    r.date,
    r.day_name,
    r.planned_minutes != null ? fmtHM(r.planned_minutes) : '—',
    fmtTime(r.check_in),
    r.break_minutes   != null ? `${r.break_minutes}m` : '—',
    fmtTime(r.check_out),
    fmtHM(r.net_worked_minutes),
    r.diff_minutes    != null ? fmtDiff(r.diff_minutes) : '—',
    STATUS_META[r.compliance_status]?.label || r.compliance_status || '—',
    r.is_adjusted ? `${t('report.corrected')} — ${r.adj_by_name || ''}: ${r.adj_reason || ''}`.trim() : '',
  ]);

  function exportCsv() {
    const lines = [EXPORT_HEADERS, ...exportRows()]
      .map(r => r.map(c => '"' + String(c).replace(/"/g, '""') + '"').join(','));
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(lines.join('\n'));
    a.download = `${fileBase}.csv`;
    a.click();
  }

  function exportExcel() {
    const title = `${wName || t('report.worker')} — ${periodLabel}`;
    const ws = XLSX.utils.aoa_to_sheet([[title], [], EXPORT_HEADERS, ...exportRows()]);
    ws['!cols'] = [10, 14, 16, 10, 13, 11, 16, 14, 14, 30].map(w => ({ wch: w }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, t('reports.workHours'));
    XLSX.writeFile(wb, `${fileBase}.xlsx`);
  }

  function exportPdf() {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
    doc.setFontSize(13);
    doc.setFont(undefined, 'bold');
    doc.text(t('report.monthlyWorkHoursTitle'), 14, 16);
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text(`${wName}  |  ${periodLabel}${identity ? `  |  ${identity}` : ''}`, 14, 23);
    doc.text(
      `${t('report.planned')}: ${fmtHM(kpi.planned)}   ${t('report.workedHours')}: ${fmtHM(kpi.worked)}` +
      `   ${t('report.difference')}: ${fmtDiff(diffTotal)}   ${t('report.overtime')}: ${fmtHM(kpi.overtime)}` +
      `   ${t('report.latenessCard')}: ${fmtHM(kpi.late)}   ${t('report.absentLabel')}: ${kpi.absent} ${t('report.days')}`,
      14, 29
    );
    autoTable(doc, {
      startY: 34,
      head: [[
        t('report.date'), t('report.dayName'), t('report.planned'),
        t('report.checkIn'), t('report.breakCol'), t('report.checkOut'),
        t('report.workedHours'), t('report.difference'), t('report.status'),
      ]],
      body: rows.map(r => [
        r.date,
        r.day_name,
        r.planned_minutes != null ? fmtHM(r.planned_minutes) : '—',
        fmtTime(r.check_in),
        r.break_minutes   != null ? `${r.break_minutes}m` : '—',
        fmtTime(r.check_out),
        fmtHM(r.net_worked_minutes),
        r.diff_minutes    != null ? fmtDiff(r.diff_minutes) : '—',
        STATUS_META[r.compliance_status]?.label || '—',
      ]),
      styles:           { fontSize: 8, cellPadding: 2 },
      headStyles:       { fillColor: [25, 118, 210], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [245, 247, 250] },
      didParseCell: ({ row, cell }) => {
        const r = rows[row.index];
        if (r && !r.is_work_day) cell.styles.textColor = [160, 160, 160];
      },
    });
    doc.save(`${fileBase}.pdf`);
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <ReportPage>

      <ReportFilters onReset={handleReset} onRefresh={handleRefresh} dirty={dirty}>
        <TextField
          select size="small" label={t('report.department')}
          value={fDept}
          onChange={e => handleDept(e.target.value)}
        >
          <MenuItem value=""><em>{t('report.allDepartments')}</em></MenuItem>
          {depts.map(d => (
            <MenuItem key={d.id || d.Id} value={String(d.id || d.Id)}>
              {d.name || d.Name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select size="small" label={t('report.worker')}
          value={fWorker}
          onChange={e => setFWorker(e.target.value)}
        >
          <MenuItem value=""><em>{t('report.selectWorker')}</em></MenuItem>
          {filteredWorkers.map(w => (
            <MenuItem key={w.Id || w.id} value={String(w.Id || w.id)}>
              {workerFullName(w)}
            </MenuItem>
          ))}
        </TextField>

        {/* Periudha — shigjetat dhe dy zgjedhësit lexohen si një kontroll i vetëm,
            ndaj rrinë ngjitur dhe nuk e marrin gjerësinë e një filtri të plotë. */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          '&& .MuiTextField-root': { minWidth: 0, flex: 'none' },
        }}>
          <Tooltip title={t('report.prevMonth')} arrow>
            <IconButton size="small" onClick={prevMonth}><ChevronLeftIcon fontSize="small" /></IconButton>
          </Tooltip>

          <TextField
            select size="small" label={t('report.month')}
            value={month}
            onChange={e => setMonth(Number(e.target.value))}
            sx={{ width: 130 }}
          >
            {Array.from({ length: 12 }, (_, i) => (
              <MenuItem key={i + 1} value={i + 1}>{t('report.month_' + (i + 1))}</MenuItem>
            ))}
          </TextField>

          <TextField
            select size="small" label={t('report.year')}
            value={year}
            onChange={e => setYear(Number(e.target.value))}
            sx={{ width: 96 }}
          >
            {YEARS.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>

          <Tooltip title={t('report.nextMonth')} arrow>
            <IconButton size="small" onClick={nextMonth}><ChevronRightIcon fontSize="small" /></IconButton>
          </Tooltip>
        </Box>
      </ReportFilters>

      {/* Kush dhe kur — titulli i faqes rri te shiriti i sipërm, ndaj kjo kokë
          mban vetëm atë që AppBar-i s'e thotë: punëtorin, orarin dhe periudhën. */}
      {loaded && (
        <Paper
          variant="outlined"
          sx={{
            px: 1.25, py: 0.875, mb: 1.25, borderRadius: 2,
            display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
          }}
        >
          <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.soft', color: 'primary.onSoft' }}>
            <PersonIcon sx={{ fontSize: 18 }} />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.25 }}>
              {wName || '—'}
            </Typography>
            {identity && (
              <Typography noWrap variant="caption" color="text.secondary">{identity}</Typography>
            )}
          </Box>
          <Chip size="small" variant="outlined" color="primary" label={periodLabel} sx={{ height: 22, fontSize: '0.6875rem' }} />
        </Paper>
      )}

      <StatTiles
        columns={4}
        items={loaded ? [
          { label: t('report.planned'),     value: fmtHM(kpi.planned) },
          { label: t('report.workedHours'), value: fmtHM(kpi.worked), accent: true },
          { label: t('report.difference'),  value: fmtDiff(diffTotal), tone: diffTotal >= 0 ? 'success' : 'error' },
          { label: t('report.overtime'),    value: fmtHM(kpi.overtime), tone: kpi.overtime > 0 ? 'info' : undefined },
          { label: t('report.breakCol'),    value: fmtHM(kpi.breaks) },
          { label: t('report.latenessCard'), value: fmtHM(kpi.late), tone: kpi.late   > 0 ? 'warning' : undefined },
          { label: t('report.absenceDays'), value: kpi.absent,        tone: kpi.absent > 0 ? 'error'   : undefined },
          { label: t('report.corrections'), value: kpi.adjusted },
        ] : []}
      />

      <ReportCard
        title={periodLabel}
        count={loaded ? kpi.workDays : undefined}
        countLabel={t('report.workDays')}
        actions={
          <ExportMenu
            onCsv={exportCsv}
            onExcel={exportExcel}
            onPdf={exportPdf}
            onPrint={() => window.print()}
            disabled={!rows.length}
          />
        }
        footer={rows.length > 0 && (
          <ReportTotals
            items={[
              { label: `${t('report.planned')}:`,     value: fmtHM(kpi.planned) },
              { label: `${t('report.workedHours')}:`, value: fmtHM(kpi.worked) },
              { label: `${t('report.difference')}:`,  value: fmtDiff(diffTotal), tone: diffTotal >= 0 ? 'success' : 'error' },
              { label: `${t('report.overtime')}:`,    value: fmtHM(kpi.overtime) },
              { label: `${t('report.workDays')}:`,    value: kpi.workDays },
            ]}
          />
        )}
      >
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700, bgcolor: 'background.paper' } }}>
              <TableCell>{t('report.date')}</TableCell>
              <TableCell>{t('report.dayName')}</TableCell>
              <TableCell className="num">{t('report.planned')}</TableCell>
              <TableCell align="center">{t('report.checkIn')}</TableCell>
              <TableCell align="center">{t('report.breakCol')}</TableCell>
              <TableCell align="center">{t('report.checkOut')}</TableCell>
              <TableCell className="num">{t('report.workedHours')}</TableCell>
              <TableCell className="num">{t('report.difference')}</TableCell>
              <TableCell align="center">{t('report.status')}</TableCell>
              {isAdmin && <TableCell align="center" sx={{ width: 52 }} />}
            </TableRow>
          </TableHead>

          <TableBody>
            {loading ? (
              <LoadingRow colSpan={COLS} />
            ) : !fWorker ? (
              <EmptyRow colSpan={COLS} message={t('report.selectWorkerToLoad')} />
            ) : rows.length === 0 ? (
              <EmptyRow colSpan={COLS} message={t('report.noDataForPeriod')} />
            ) : weekGroups.map(({ key, rows: wRows }) => (
              <React.Fragment key={key}>
                {wRows.map(row => {
                  const isOff  = !row.is_work_day;
                  const isAdj  = row.is_adjusted;
                  const ded    = Number(row.deduction_minutes) || 0;
                  const status = STATUS_META[row.compliance_status] || { label: row.compliance_status, color: 'default' };

                  return (
                    <TableRow
                      key={row.date}
                      hover
                      sx={{
                        opacity: isOff ? 0.55 : 1,
                        bgcolor: isOff ? 'action.hover' : 'inherit',
                      }}
                    >
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: isOff ? 400 : 600, color: isOff ? 'text.disabled' : 'text.primary' }}>
                        {dayFmt(row.date)}
                      </TableCell>

                      <TableCell sx={{ whiteSpace: 'nowrap', color: isOff ? 'text.disabled' : 'text.secondary' }}>
                        {row.day_name}
                      </TableCell>

                      <TableCell className="num" sx={{ color: 'text.secondary' }}>
                        {row.planned_minutes != null ? fmtHM(row.planned_minutes) : '—'}
                      </TableCell>

                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {fmtTime(row.check_in)}
                      </TableCell>

                      <TableCell align="center">
                        {row.break_minutes != null ? `${row.break_minutes}m` : '—'}
                      </TableCell>

                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {fmtTime(row.check_out)}
                      </TableCell>

                      <TableCell className="num" sx={{ fontWeight: 600 }}>
                        {fmtHM(row.net_worked_minutes)}
                      </TableCell>

                      <TableCell
                        className="num"
                        sx={{
                          fontWeight: 600,
                          color: row.diff_minutes != null
                            ? (row.diff_minutes >= 0 ? 'success.main' : 'error.main')
                            : 'text.disabled',
                        }}
                      >
                        {row.diff_minutes != null ? fmtDiff(row.diff_minutes) : '—'}
                      </TableCell>

                      <TableCell align="center">
                        <Stack direction="row" spacing={0.5} justifyContent="center" flexWrap="wrap" useFlexGap>
                          <Chip
                            label={status.label}
                            color={status.color}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.6875rem' }}
                          />
                          {isAdj && (
                            <Tooltip title={`${t('report.correctedBy')} ${row.adj_by_name || '—'}: ${row.adj_reason || ''}`} arrow>
                              <Chip
                                label={t('report.corrAbbr')}
                                color="secondary"
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.6875rem', cursor: 'help' }}
                              />
                            </Tooltip>
                          )}
                          {/* Zbritja automatike hiqet nga orët e punuara te serveri.
                              Pa këtë shenjë, dita dilte thjesht me më pak orë — pa
                              asnjë gjurmë se pse. */}
                          {ded > 0 && (
                            <Tooltip
                              arrow
                              title={`${t('report.deductionApplied')}: ${fmtHM(ded)}${row.deduction_reason ? ` — ${row.deduction_reason}` : ''}`}
                            >
                              <Chip
                                label={`−${fmtHM(ded)}`}
                                color="warning"
                                size="small"
                                variant="outlined"
                                sx={{ height: 20, fontSize: '0.6875rem', cursor: 'help' }}
                              />
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>

                      {isAdmin && (
                        <TableCell align="center">
                          {!isOff && (
                            <Tooltip title={t('report.correctDay')} arrow>
                              <IconButton
                                size="small"
                                onClick={() => { setAdjRow(row); setAdjOpen(true); }}
                              >
                                <EditIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}

                {/* Weekly summary row after each week */}
                <WeeklySummaryRow weekRows={wRows} isAdmin={isAdmin} />
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </ReportCard>

      {/* Adjustment Dialog — reuses the existing component without modification */}
      {adjRow && (
        <AttendanceAdjustmentDialog
          open={adjOpen}
          onClose={() => setAdjOpen(false)}
          row={adjRow}
          workerName={wName}
          onSaved={() => {
            setAdjOpen(false);
            handleLoad();
          }}
        />
      )}
    </ReportPage>
  );
}
